import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CheckCircle, AlertTriangle, Shield, FileText } from 'lucide-react';
import api from '../../services/api';
import { usePageTitle } from '../../hooks/usePageTitle';

export default function WhatsAppTermsPage() {
  usePageTitle('Termos de Uso - Integração WhatsApp');
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleAccept = async () => {
    if (!accepted) {
      toast.error('Você precisa concordar com os termos para continuar');
      return;
    }

    setLoading(true);
    try {
      await api.post('/whatsapp/accept-terms');
      toast.success('Termos aceitos com sucesso!');
      
      // Redirecionar para a página do WhatsApp após 1 segundo
      setTimeout(() => {
        window.close(); // Tenta fechar a aba se foi aberta por target="_blank"
        navigate('/admin/whatsapp'); // Fallback caso não consiga fechar
      }, 1000);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao aceitar termos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Shield className="h-16 w-16 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Termos de Uso - Integração WhatsApp
          </h1>
          <p className="text-gray-600">
            Conheça como funciona nossa integração e suas responsabilidades
          </p>
        </div>

        {/* Terms Content */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          {/* Info Box */}
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-blue-400 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-800 mb-1">
                  Sobre a Integração
                </h3>
                <p className="text-sm text-blue-700">
                  Nossa integração permite que você envie mensagens personalizadas através do WhatsApp 
                  para seus clientes. Implementamos limites e boas práticas para garantir o melhor uso.
                </p>
              </div>
            </div>
          </div>

          {/* Terms Sections */}
          <div className="space-y-6 text-gray-700 h-96 overflow-y-auto">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                1. Aceite e Concordância
              </h2>
              <p className="mb-2">
                Ao aceitar estes termos e utilizar a funcionalidade de integração com WhatsApp 
                ("Serviço"), você ("Usuário") declara estar ciente e concordar com todas as 
                condições aqui estabelecidas.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                2. Natureza do Serviço
              </h2>
              <p className="mb-2">
                O Serviço oferecido pela plataforma Roda de Prêmios utiliza tecnologia de integração 
                com WhatsApp para permitir o envio de mensagens aos seus clientes.
              </p>
              <p>
                <strong>O Usuário reconhece que:</strong>
              </p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>
                  Esta é uma ferramenta de automação que deve ser usada com responsabilidade
                </li>
                <li>
                  O WhatsApp pode aplicar suas políticas de uso a qualquer momento
                </li>
                <li>
                  Implementamos limites de envio para ajudar a manter boas práticas
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                3. Responsabilidades do Usuário
              </h2>
              <p className="mb-2">
                <strong>Ao utilizar este serviço, você é responsável por:</strong>
              </p>
              <ul className="list-disc ml-6 space-y-2">
                <li>
                  <strong>Uso adequado:</strong> Enviar apenas mensagens relevantes e autorizadas 
                  pelos destinatários.
                </li>
                <li>
                  <strong>Conteúdo das mensagens:</strong> O conteúdo, frequência e destinatários 
                  das mensagens enviadas.
                </li>
                <li>
                  <strong>Conformidade legal:</strong> Garantir que seu uso está em conformidade 
                  com as leis aplicáveis (LGPD, GDPR) e políticas do WhatsApp.
                </li>
                <li>
                  <strong>Práticas éticas:</strong> Não enviar spam ou mensagens não solicitadas 
                  em grande volume.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                4. Isenção de Responsabilidade
              </h2>
              <p className="mb-2">
                <strong>O Roda de Prêmios não se responsabiliza por:</strong>
              </p>
              <ul className="list-disc ml-6 space-y-2">
                <li>
                  Banmentos, restrições ou limitações aplicadas pelo WhatsApp/Meta à conta do Usuário
                </li>
                <li>
                  Prejuízos financeiros, comerciais ou de qualquer natureza decorrentes do uso do Serviço
                </li>
                <li>
                  Indisponibilidade, interrupção ou descontinuação do Serviço
                </li>
                <li>
                  Alterações nas políticas ou funcionamento do WhatsApp que afetem o Serviço
                </li>
                <li>
                  Violações de privacidade ou vazamento de dados
                </li>
                <li>
                  Ações legais, processos ou penalidades decorrentes do uso inadequado do Serviço
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                5. Uso Adequado e Conformidade Legal
              </h2>
              <p className="mb-2">
                O Usuário se compromete a:
              </p>
              <ul className="list-disc ml-6 space-y-1">
                <li>
                  Utilizar o Serviço apenas para fins legítimos e lícitos
                </li>
                <li>
                  Obter consentimento prévio dos destinatários antes de enviar mensagens comerciais
                </li>
                <li>
                  Respeitar as leis de proteção de dados (LGPD, GDPR, etc.)
                </li>
                <li>
                  Não utilizar o Serviço para spam, fraudes ou atividades maliciosas
                </li>
                <li>
                  Respeitar o limite de 10 mensagens por disparo
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                6. Funcionalidades e Limites
              </h2>
              <p>
                Para garantir boas práticas, implementamos:
              </p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>
                  Máximo de 10 destinatários por disparo de mensagens
                </li>
                <li>
                  Intervalos entre mensagens para evitar detecção como spam
                </li>
                <li>
                  Recomendações de boas práticas para uso seguro
                </li>
              </ul>
              <p className="mt-2">
                <strong>Importante:</strong> Mesmo com estas limitações, o risco de banimento 
                persiste e é de responsabilidade exclusiva do Usuário.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                7. Recomendações de Segurança
              </h2>
              <p className="mb-2">
                Para minimizar riscos, recomendamos:
              </p>
              <ul className="list-disc ml-6 space-y-1">
                <li>
                  Use uma conta WhatsApp separada para testes e automações
                </li>
                <li>
                  Evite usar sua conta pessoal ou principal de negócios
                </li>
                <li>
                  Monitore regularmente o status da sua conexão
                </li>
                <li>
                  Respeite os limites de mensagens estabelecidos
                </li>
                <li>
                  Envie apenas mensagens relevantes e autorizadas
                </li>
                <li>
                  Mantenha backup dos seus dados importantes
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                8. Alterações nos Termos
              </h2>
              <p>
                O Roda de Prêmios reserva-se o direito de modificar estes termos a qualquer momento. 
                O uso continuado do Serviço após alterações constitui aceitação dos novos termos.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                9. Descontinuação do Serviço
              </h2>
              <p>
                O Roda de Prêmios pode descontinuar o Serviço a qualquer momento, sem aviso prévio, 
                sem que isso gere qualquer direito à indenização ou compensação ao Usuário.
              </p>
            </section>

            <section className="border-t-2 border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                10. Declaração de Ciência e Concordância
              </h2>
              <p className="font-medium text-gray-900">
                Ao aceitar estes termos, você declara ter compreendido como funciona a integração 
                e concorda em utilizar o serviço de forma responsável e em conformidade com as leis 
                aplicáveis, isentando o Roda de Prêmios de responsabilidade por consequências 
                decorrentes do uso.
              </p>
            </section>
          </div>
        </div>

        {/* Acceptance Checkbox and Button */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-start mb-4">
            <input
              type="checkbox"
              id="accept-terms"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="accept-terms" className="ml-3 text-sm text-gray-700">
              <strong>Li e concordo com todos os termos acima.</strong> Estou ciente dos riscos 
              e assumo total responsabilidade pelo uso desta funcionalidade, isentando o Roda de 
              Prêmios de qualquer responsabilidade por banimentos, suspensões ou prejuízos 
              decorrentes do uso da integração WhatsApp.
            </label>
          </div>

          <button
            onClick={handleAccept}
            disabled={!accepted || loading}
            className="w-full flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Processando...
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 mr-2" />
                Aceitar e Começar a Usar
              </>
            )}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            Data e hora do aceite, endereço IP e informações do navegador serão registrados 
            para fins de auditoria e conformidade legal.
          </p>
        </div>
      </div>
    </div>
  );
}
